<template>
  <div
    :data-testid="dataTestid"
    :class="[
      component.main,
      component.position,
      $attrs.class
    ]"
  >
    <ul :class="component.parentClasses">
      <template
        v-for="(tab, tabIndex) in tabs"
        :key="tabIndex"
      >
        <li
          v-if="tab?.label || tab?.iconComponent"
          :class="[
            ...component.tabClasses,
            {
              [`font-bold ${activeBgClass} text-semantic-surface-on-surface`]:
                modelValue === tab?.key && !inverted,
              'font-bold bg-semantic-surface-default text-semantic-surface-on-surface':
                modelValue === tab?.key && inverted,
              'w-full': props.variant === 'fill-width',
            },
          ]"
          @click="modelValue = tab?.key"
        >
          <div class="flex items-center justify-center gap-2">
            <Component
              :is="tab.iconComponent"
              v-if="tab?.iconComponent"
              width="24"
              height="24"
              :fill="iconFill(tab.key)"
            />

            <p v-if="tab.label">
              {{ tab.label }}
            </p>

            <V2MoleculesPopover v-if="tab?.tooltip">
              <template
                v-if="tab?.tooltip?.title"
                #title
              >
                {{ tab?.tooltip?.title }}
              </template>

              <template
                v-if="tab?.tooltip?.content"
                #description
              >
                {{ tab?.tooltip?.content }}
              </template>
            </V2MoleculesPopover>
          </div>
        </li>
      </template>
    </ul>
  </div>

  <slot :name="modelValue" />
</template>

<script setup>
defineOptions({
  name: 'V2MoleculesTabs'
})

const $attrs = useAttrs()

const props = defineProps({
  dataTestid: {
    type: String,
    default: ''
  },

  inverted: {
    type: Boolean,
    default: false
  },

  // This props will work when variant is set to fit-content
  position: {
    type: String,
    default: 'center',
    validator: value => ['left', 'center', 'right'].includes(value)
  },

  size: {
    type: String,
    default: 'base',
    validator: value => ['base', 'large'].includes(value)
  },

  variant: {
    type: String,
    default: 'fit-content',
    validator: value => ['fill-width', 'fit-content'].includes(value)
  },

  // background applied to the active tab when not inverted
  activeBgClass: {
    type: String,
    default: 'bg-base-lavender-400'
  },

  tabs: {
    type: Array,
    default: () => [
      { 
        label: 'Tab 1',
        key: 'tab-1'
      },
      { 
        label: 'Tab 2',
        key: 'tab-2',
        tooltip: {
          title: 'Title',
          content: 'Lorem ipsum dolor, sit amet consectetur adipisicing elit. Quasi dicta quo tenetur officiis dolores aperiam fugit fugiat, quam praesentium aliquam ad illo magnam esse voluptatem nostrum repudiandae possimus quidem repellat.'
        }
      }
    ]
  }
})

const modelValue = defineModel('modelValue', {
  type: String,
  default: ''
})

const defaultTab = computed(() => {
  if (modelValue.value) {
    return modelValue.value
  }

  const tab = props.tabs?.[0]
  return tab?.key || tab ? strToKebabCase(tab?.key || tab) : ''
})

if (!modelValue.value) {
  modelValue.value = defaultTab.value
}

const component = computed(() => {
  const main = (() => {
    if (props.variant === 'fill-width') {
      return 'max-w-full'
    }

    return 'max-w-fit'
  })()

  const position = (() => {
    const map = {
      left: 'mr-auto',
      center: 'mx-auto',
      right: 'ml-auto'
    }
    return map[props.position] || map.center
  })()

  const parentClasses = (() => {
    const classes = ['flex', 'border', 'border-semantic-outline-variant']

    if (props.size === 'base') {
      classes.push(...['p-1', 'rounded-xl', 'gap-1'])
    } else {
      classes.push(...['p-2', 'rounded-3xl', 'gap-2'])
    }

    if (props.inverted) {
      classes.push('bg-semantic-inverse-container-lowest')
    } else {
      classes.push('bg-semantic-surface-container-variant')
    }

    return classes
  })()

  const tabClasses = (() => {
    const classes = ['cursor-pointer', 'text-center']

    if (props.size === 'large') {
      classes.push(...['text-semantic-sizing-body', 'rounded-2xl', 'py-3', 'px-6'])
    } else {
      classes.push(...['text-semantic-sizing-body-sm', 'rounded-2md', 'py-3', 'px-6'])
    }

    if (props.inverted) {
      classes.push('text-semantic-inverse-on-surface')
    } else {
      classes.push('text-semantic-surface-on-surface')
    }

    return classes
  })()

  return {
    main,
    position,
    parentClasses,
    tabClasses
  }
})

const iconFill = key => {
  const selected = modelValue.value === key

  if (!props.inverted) {
    return semanticColorLookup('surface.on-surface')
  } 

  return selected
    ? semanticColorLookup('surface.on-surface')
    : semanticColorLookup('inverse.on-surface')
}
</script>
